import React, { useState } from 'react'
import { ApiRequest } from '../../../../services/api/api'
import { nbsStr } from '../../../../services/lib/utils/helpers'
import moment from 'moment'
import ItipsForRevDetails from '../../../../components/common/i-tips/receiving-details/index'
import { ThunderboltOutlined, FileTextOutlined } from '@ant-design/icons'
import { getRandomKey } from '../../../../services/lib/utils/helpers'
import PurchaseOrderReceivingModal from './purchase-order-receiving-modal'
import PurchaseOrderPutawayModal from './purchase-order-putaway-modal'

export const fetchOptionsArray = async (): Promise<{ value: number; label: string; price?: number }[]> => {
  const res = await ApiRequest({
    url: 'ProductOption/GetAllProductOption',
    method: 'get',
    isShowSpinner: false
  })
  const list = res?.data?.data || []
  return list.map((o: any) => ({ value: o.optionId, label: o.optionName, price: o.price }))
}

// Module-level label map for rendering labels without caching arrays
const productOptionLabelMapModule: Record<number, string> = {}
// Populate once at module load
fetchOptionsArray().then(list => {
  list.forEach(o => { productOptionLabelMapModule[o.value] = o.label })
}).catch(() => {})

const myfiler=(
  filterValue:any,
  rowData:any
  ) => {
  const filter = filterValue.replace(/-/g, '\u2011').trim().slice(0,-1).toUpperCase();
  for (let ele of rowData.poDetail){
    const prodName = ele?.rawMaterial.rawMaterialCode?.toUpperCase().replace(/-/g, '\u2011') + ele?.rawMaterial.rawMaterialName?.toUpperCase().replace(/-/g, '\u2011')
    if (prodName.indexOf(filter) >=0) return true;
  }
  return false
  }  
  const renderStatus = (statusId: any) => {
    const statusConfig: Record<number, { label: string; className: string }> = {
      1: { label: 'Pending', className: 'jc-po-status--pending' },
      2: { label: 'Approved', className: 'jc-po-status--approved' },
      3: { label: 'Completed', className: 'jc-po-status--completed' },
      4: { label: 'Receiving', className: 'jc-po-status--receiving' }
    }

    const config = statusConfig[statusId] || { label: 'Unknown', className: 'jc-po-status--unknown' }

    return (
      <span className={`jc-po-status ${config.className}`}>
        {config.label}
      </span>
    )
  }

// 明细行进度色条: 灰=未收 · 蓝=收货中 · 红=全部完成 · 橙=数量不符
const rowAccentClass = (row: any) => {
  const ord = row.quantity || 0
  const rcv = row.totalRevQty || 0
  const pw = row.totalHandledQty || 0

  // 1. Rcv=0 → 未开始
  if (rcv === 0) {
    return 'jc-po-detail-row--start'
  }

  // 2. Rcv≠0 且 Rcv>P/W → 收货中
  if (rcv > 0 && rcv > pw) {
    return 'jc-po-detail-row--progress'
  }

  // 3. Rcv=P/W 且 Ord=Rcv → 全部完成
  if (rcv === pw && ord === rcv) {
    return 'jc-po-detail-row--done'
  }

  // 4. Rcv=P/W 且 Ord≠Rcv → 数量不符
  if (rcv === pw && ord !== rcv) {
    return 'jc-po-detail-row--mismatch'
  }

  return 'jc-po-detail-row--start'
}

const renderPurchaseOrderDetails = (rowData: any, type?: any, showUnitPrice?: boolean) => {
  const isPayable = type === 3
  const shouldShowUnitPrice = showUnitPrice !== undefined ? showUnitPrice : (type !== 4) // 默认接收页面不显示单价
  const currencySymbolMap: Record<number, string> = { 1: '$', 2: '¥', 3: '$' }
  const currencySymbol = currencySymbolMap[rowData.suplier?.currencyId] || '$'

  return (
    <div className="jc-po-detail" style={{ padding: isPayable ? '1px 0' : '2px 0', overflow: 'hidden' }}>
      {/* 原材料明细 */}
      {rowData.poDetail?.map((row: any, index: number) => {
        const materialText = nbsStr(
          row.rawMaterial?.rawMaterialCode + ' / ' + row.rawMaterial?.rawMaterialName,
          false,
          65
        )
        const materialFullText = row.rawMaterial?.rawMaterialCode + ' / ' + row.rawMaterial?.rawMaterialName

        return (
          <div
            key={`detail-${index}`}
            className={`jc-po-detail-row ${rowAccentClass(row)}`}
            title={materialFullText}
          >
            {/* 物料信息（允许换行） */}
            <span className="jc-po-detail-name" style={{ minWidth: isPayable ? 240 : (type === 4 ? 200 : 300) }}>
              {materialText}
            </span>

            {/* 单价标签（不换行） */}
            {shouldShowUnitPrice && (
              <div className="jc-po-up">
                U.P: {currencySymbol}{row.unitPrice}
              </div>
            )}

            {/* 数量统计（不换行） */}
            <div className="jc-po-qty">
              <div className="jc-po-qty-pill jc-po-qty--ord">Ord:<b>{row.quantity}</b></div>
              <div className="jc-po-qty-pill jc-po-qty--rcv">Rcv:<b>{row.totalRevQty}</b></div>
              <div className="jc-po-qty-pill jc-po-qty--pw">P/W:<b>{row.totalHandledQty}</b></div>
            </div>
          </div>
        )
      })}

      {/* 选项明细 */}
      {rowData.poOption?.map((row: any, index: number) => {
        const label = productOptionLabelMapModule[row.optionId] || row.optionId
        return (
          <div
            key={`option-${index}`}
            className="jc-po-option"
          >
            {label}:{' '}
            <strong>{currencySymbol}{row.price}</strong>
          </div>
        )
      })}
    </div>
  )
}

// Price 渲染组件
const renderPurchaseOrderPrice = (rowData: any) => {
  const optionTotal = rowData.poOption?.reduce((a: number, o: any) => a + o?.price, 0) || 0
  const currencyMap: Record<number, string> = { 1: 'NZD', 2: 'RMB', 3: 'USD' }
  const currencySymbolMap: Record<number, string> = { 1: '$', 2: '¥', 3: '$' }
  const currency = currencyMap[rowData.suplier.currencyId] || 'Unknown'
  const currencySymbol = currencySymbolMap[rowData.suplier.currencyId] || '$'
  const isNZD = rowData.suplier.currencyId === 1
  const basePrice = rowData.price + optionTotal

  return (
    <div className="jc-po-price">
      <div className="jc-po-price-chip">{currency}</div>
      <div className="jc-po-price-main">
        {currencySymbol}{basePrice.toFixed(2)}
        {isNZD && <small>+GST</small>}
      </div>
      {isNZD && (
        <div className="jc-po-price-sub">
          {currencySymbol}{(basePrice * 1.15).toFixed(2)} <small>inc</small>
        </div>
      )}
    </div>
  )
}

// Payable页面的Price列渲染组件（包含报价和实际价格）
const renderPayablePrice = (rowData: any) => {
  const optionTotal = rowData.poOption?.reduce((a: number, o: any) => a + o?.price, 0) || 0
  const currencyMap: Record<number, string> = { 1: 'NZD', 2: 'RMB', 3: 'USD' }
  const currencySymbolMap: Record<number, string> = { 1: '$', 2: '¥', 3: '$' }
  const currency = currencyMap[rowData.suplier.currencyId] || 'Unknown'
  const currencySymbol = currencySymbolMap[rowData.suplier.currencyId] || '$'
  const isNZD = rowData.suplier.currencyId === 1
  const actualPrice = rowData.actualInclGstPrice || rowData.actualPrice || 0
  const actualGst = rowData.actualGst || 0

  return (
    <div className="jc-po-price-duo" style={{ padding: '4px 0' }}>
      {/* 左侧：报价 */}
      <div className="jc-po-price-col">
        <div className="jc-po-price-chip">{currency} Quote</div>
        <div className="jc-po-price-main">
          {isNZD && <small>Excl GST </small>}
          {currencySymbol}{(rowData.price + optionTotal).toFixed(2)}
        </div>
        {isNZD && (
          <div className="jc-po-price-sub">
            <small>Incl GST </small>
            {currencySymbol}{((rowData.price + optionTotal) * 1.15).toFixed(2)}
          </div>
        )}
      </div>

      {/* 分隔线 */}
      <div className="jc-po-price-divider" />

      {/* 右侧：实际价格（与报价格式一致） */}
      {actualPrice > 0 && (
        <div className="jc-po-price-col jc-po-price--actual">
          <div className="jc-po-price-chip">{currency} Actual</div>
          <div className="jc-po-price-main">
            {isNZD && <small>Excl GST </small>}
            {currencySymbol}{(isNZD ? (actualPrice - actualGst).toFixed(2) : actualPrice.toFixed(2))}
          </div>
          {isNZD && actualGst > 0 && (
            <div className="jc-po-price-sub">
              <small>Incl GST </small>
              {currencySymbol}{actualPrice.toFixed(2)}
            </div>
          )}
          {!isNZD && actualPrice > 0 && (
            <div style={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#10b981'
            }}>
              {currencySymbol}{actualPrice.toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const PurchaseOrderColumnModel = (type:any, onOpenReceivingModal?: (poData: any) => void, onOpenPutawayModal?: (poData: any) => void, onOpenInspectionLog?: (poData: any) => void): any => {
  // 接收页面使用简化的列配置
  if (type === 4) {
    return [
      {
        title: 'PO Number',
        field: 'poNo',
        width: 120,
        render: (rowData: any) => (
          <div style={{ fontWeight: 600, color: '#1f2937', fontSize: '14px' }}>
            {rowData.poNo}
          </div>
        )
      },
      {
        title: 'Items',
        field: 'poDetail',
        width: 450,
        filtering: true,
        render: (rowData: any) => renderPurchaseOrderDetails(rowData, type, false), // 接收页面不显示单价
        customFilterAndSearch: (filterValue: any, rowData: any) => myfiler(filterValue, rowData)
      },
      {
        title: 'Supplier',
        field: 'supplierName',
        width: 220
      },
      {
        title: 'ETA',
        field: 'deliveryDate1',
        width: 100,
        render: (rowData: any) => {
          // 检查日期是否有效
          let displayDate = 'N/A'
          if (rowData.deliveryDate) {
            try {
              const date = moment.utc(rowData.deliveryDate)
              if (date.isValid()) {
                displayDate = date.local().format('DD/MM/YYYY')
              }
            } catch (e) {
              console.error('Invalid date:', rowData.deliveryDate)
            }
          }

          return (
            <div style={{ fontSize: '13px', color: displayDate === 'N/A' ? '#9ca3af' : '#374151' }}>
              {displayDate}
            </div>
          )
        }
      },
      {
        title: 'Status',
        field: 'poStatusId',
        width: 120,
        defaultFilter: ['2', '4'], // 默认显示 Approved, Receiving
        lookup: { 1: 'Pending', 2: 'Appr', 3: 'Comp', 4: 'Recv' },
        render: (rowData: any) => renderStatus(rowData.poStatusId)
      },
      {
        title: 'Notes',
        field: 'notes',
        width: 150,
        render: (rowData: any) => (
          <div style={{ fontSize: '13px', color: '#374151' }}>
            {rowData.notes || '-'}
          </div>
        )
      },
      {
        title: 'Actions',
        field: 'receivingActions',
        width: 200,
        render: (rowData: any) => renderReceivingActionButtons(rowData, onOpenReceivingModal, onOpenPutawayModal, onOpenInspectionLog)
      }
    ]
  }

  // 其他页面类型使用原有的列配置
  let columns = [
    {
      title: 'PO Number',
      field: 'poNo'
    },
    {
      title: 'Details',
      field: 'poDetail',
      filtering: true,
      render: (rowData: any) => renderPurchaseOrderDetails(rowData, type),

      customFilterAndSearch:(        filterValue:any,
        rowData:any) =>myfiler(
        filterValue,
        rowData
        )
    },

    {
      title: 'PO Price',
      field: 'price',
      width: 150,
      render: renderPurchaseOrderPrice
    },
    ...(type === 3
      ? [
          {
            title: 'Actual Price',
            field: 'actualInclGstPrice',
            width: 150,
            render: (rowData: any) => {
              const actualPrice = rowData.actualInclGstPrice || rowData.actualPrice || 0
              const actualGst = rowData.actualGst || 0
              const currencyMap: Record<number, string> = { 1: 'NZD', 2: 'RMB', 3: 'USD' }
              const currencySymbolMap: Record<number, string> = { 1: '$', 2: '¥', 3: '$' }
              const currency = currencyMap[rowData.suplier?.currencyId] || 'Unknown'
              const currencySymbol = currencySymbolMap[rowData.suplier?.currencyId] || '$'
              const isNZD = rowData.suplier?.currencyId === 1
              const exclPrice = isNZD ? (actualPrice - actualGst) : actualPrice

              return (
                <div className="jc-po-price jc-po-price--actual">
                  <div className="jc-po-price-chip">{currency} Actual</div>
                  <div className="jc-po-price-main">
                    {currencySymbol}{exclPrice.toFixed(2)}
                    {isNZD && actualGst > 0 && <small>+GST</small>}
                  </div>
                  {isNZD && actualGst > 0 && (
                    <div className="jc-po-price-sub">
                      {currencySymbol}{actualPrice.toFixed(2)} <small>inc</small>
                    </div>
                  )}
                </div>
              )
            }
          },
        ]
      : []),

    {
      title: 'Supplier',
      field: 'supplierName'
    },
  ...(type === 1 || type === 2
      ? [
          {
            title: 'ETA',
            field: 'deliveryDate1',
            width: 120,
            render: (rowData: any) => {
              // 检查日期是否有效
              let displayDate = 'N/A'
              if (rowData.deliveryDate) {
                try {
                  const date = moment.utc(rowData.deliveryDate)
                  if (date.isValid()) {
                    displayDate = date.local().format('DD/MM/YYYY')
                  }
                } catch (e) {
                  console.error('Invalid date:', rowData.deliveryDate)
                }
              }

              return (
                <div style={{ fontSize: '13px', color: displayDate === 'N/A' ? '#9ca3af' : '#374151' }}>
                  {displayDate}
                </div>
              )
            }
          }
        ]
      : []),    
    {
      title: 'Created By',
      field: 'createdEmployeeName',
      width: 150,
      render: (rowData: any) => (
        <div>
          <div style={{
            fontWeight: 500,
            color: '#1f2937',
            fontSize: '13px',
            marginBottom: '2px'
          }}>
            {rowData.createdEmployeeName}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            {moment.utc(rowData.createdAt).local().format('DD/MM/YYYY')}
          </div>
        </div>
      )
    },
    // {
    //   title: 'Delivery Addr',
    //   field: 'deliveryAddr'
    // },
    // {
    //   title: 'Delivery Date',
    //   field: 'deliveryDate1',
    // },
    // {
    //   title: 'Created At',
    //   field: 'createdAt',
    //   render: (rowData: any) => moment.utc(rowData.createdAt).local().format('DD/MM/YYYY')
    // },
    // {
    //   title: 'Details',
    //   field: 'poId',
    //   render: (rowData: any) => (
    //     <ItipsForRevDetails id={rowData.poId}/>
    //   )
    // },
    // {
    //   title: 'Payable',
    //   field: 'payable',
    //   filtering: true,
    //   defaultFilter: ['1'], // 默认显示"不可付款"的
    //   lookup: { 0: 'Not Payable', 1: 'Payable' },
    //   render: (rowData: any) => {
    //     const allHandled = rowData.poDetail?.every((row: any) => row.totalHandledQty === row.quantity);
    //     return (
    //       <div style={{ color: allHandled ? 'green' : 'gray', fontWeight: 500 }}>
    //         {allHandled ? 'Payable' : 'Not Payable'}
    //       </div>
    //     );
    //   },
    //   customFilterAndSearch: (filterValue: any, rowData: any) => {
    //     const allHandled = rowData.poDetail?.every((row: any) => row.totalHandledQty === row.quantity);

    //     // filterValue 是数组，包含 ['0'] 或 ['1'] 或 ['0','1']
    //     if (filterValue.includes('1') && allHandled) return true;
    //     if (filterValue.includes('0') && !allHandled) return true;
    //     return false;
    //   }
    // },
    {
      title: 'Notes',
      field: 'notes',
    },
    {
      title: 'Status',
      field: 'poStatusId',
      ...(type === 3
        ? { defaultFilter: ['3'] }
        : type === 4
          ? { defaultFilter: ['2', '4'] }
          : { defaultFilter: ['1', '2', '4'] }
      ),
      lookup: { 1: 'Pending', 2: 'Appr', 3: 'Comp', 4: 'Recv' },
      render: (rowData: any) => {
        if (type === 3) {
          // Payable页面：Status + Log按钮
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              {renderStatus(rowData.poStatusId)}
              <button
                key={getRandomKey()}
                type="button"
                className="jc-btn jc-btn--slate jc-btn--sm"
                onClick={() => onOpenInspectionLog?.(rowData)}
              >
                <FileTextOutlined /> Log
              </button>
            </div>
          )
        }
        return renderStatus(rowData.poStatusId)
      }
    },
    ...(type === 3
      ? [
          {
            title: 'Payment',
            field: 'paid',
            defaultFilter: ['0'],
            lookup: { 0: 'Unpaid', 1: 'Paid' },
            render: (rowData: any) => (
              <div>{rowData.paid === 1 ? 'Paid' : 'Unpaid'}</div>
            ),
          },
          {
            title: 'Invoice Note',
            field: 'invoiceNote',
          },
        ]
      : []), // Conditionally add columns for type === 3
  ]

  return columns
}

// 接收页面的操作按钮渲染组件
const renderReceivingActionButtons = (rowData: any, onOpenReceivingModal?: (poData: any) => void, onOpenPutawayModal?: (poData: any) => void, onOpenInspectionLog?: (poData: any) => void) => {
  const isInReceiving = rowData.poStatusId === 4  // poStatusId=4 对应 "Receiving" 状态

  const miniBtn = (props: { label: string; className: string; icon?: any; onClick?: () => void; title?: string }) => (
    <button
      key={getRandomKey()}
      type="button"
      className={`jc-btn ${props.className}`}
      title={props.title}
      onClick={props.onClick}
    >
      {props.icon}
      {props.label}
    </button>
  )

  // Receiving 状态: RCV / P/W / Log (不含 Gen. U/N)
  if (isInReceiving) {
    return (
      <div className="jc-po-actions--2">
        {miniBtn({
          label: 'RCV',
          className: 'jc-btn--solid-orange',
          icon: <ThunderboltOutlined />,
          title: 'Receive Goods',
          onClick: () => onOpenReceivingModal && onOpenReceivingModal(rowData)
        })}
        {miniBtn({
          label: 'P/W',
          className: 'jc-btn--solid-blue',
          icon: <ThunderboltOutlined />,
          title: 'Putaway',
          onClick: () => onOpenPutawayModal?.(rowData)
        })}
        {miniBtn({
          label: 'Log',
          className: 'jc-btn--slate',
          icon: <FileTextOutlined />,
          title: 'Inspection Log',
          onClick: () => onOpenInspectionLog?.(rowData)
        })}
      </div>
    )
  }

  // Approved 状态: RCV / P/W / Log / Gen. U/N
  return (
    <div className="jc-po-actions--2">
      {miniBtn({
        label: 'RCV',
        className: 'jc-btn--solid-orange',
        icon: <ThunderboltOutlined />,
        title: 'Receive Goods',
        onClick: () => onOpenReceivingModal?.(rowData)
      })}
      {miniBtn({
        label: 'P/W',
        className: 'jc-btn--solid-blue',
        icon: <ThunderboltOutlined />,
        title: 'Putaway',
        onClick: () => onOpenPutawayModal?.(rowData)
      })}
      {miniBtn({
        label: 'Log',
        className: 'jc-btn--slate',
        icon: <FileTextOutlined />,
        title: 'Inspection Log',
        onClick: () => onOpenInspectionLog?.(rowData)
      })}
      {miniBtn({
        label: 'Gen. U/N',
        className: 'jc-btn--green',
        title: 'Generate Unit Number'
      })}
    </div>
  )
}
